"use client";

import * as React from "react";
import { Shield, Layers, Sparkles, ArrowRight, Flame, Hammer, Wind, Home, Droplets, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface RoofingServiceItem {
  key: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  link: string;
  image: string;
  badge: string;
}
const roofingServices: RoofingServiceItem[] = [{
  key: "roofing-services",
  title: "Roofing Services",
  description: "Complete roofing solutions across Southampton, covering structural repairs, leak investigations, ridge maintenance, and complete pitched roof overhauls.",
  icon: Shield,
  link: "/RoofingServicesPage",
  image: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/new-roof.jpg?v=8qmpfco9j2v",
  badge: "Full Service"
}, {
  key: "fascias-soffits",
  title: "Fascias & Soffits",
  description: "Low-maintenance uPVC fascia boards, ventilated soffits, and durable bargeboards engineered to protect roofline rafters from moisture and weather decay.",
  icon: Wind,
  link: "/FasciasSoffitsPage",
  image: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/new-facias-soffits-gutters.jpg?v=0io3fig2tp",
  badge: "Roofline"
}, {
  key: "guttering",
  title: "Guttering",
  description: "New and replacement guttering installations, high-capacity downpipes, joint leak repairs, and seasonal clearing built for South Coast downpours.",
  icon: Droplets,
  link: "/GutteringPage",
  image: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/10/roofer-replacing-guttering-southampton.jpg",
  badge: "Rainwater"
}, {
  key: "flat-roofs",
  title: "Flat Roofs",
  description: "Modern GRP fibreglass and durable EPDM rubber flat roof systems engineered for seamless watertight protection on extensions, dormers, and garages.",
  icon: Layers,
  link: "/FlatRoofsPage",
  image: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/flat-roof.webp?v=t143rwcbvdo",
  badge: "EPDM & GRP"
}, {
  key: "new-roofs",
  title: "New Roofs",
  description: "Precision pitched slate and tile roof installations for residential properties, built with high-grade breathable membranes, treated battens, and durable fixings.",
  icon: Home,
  link: "/NewRoofsPage",
  image: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/new-roof-work.jpg?v=mt34xrimrx",
  badge: "Tile & Slate"
}, {
  key: "chimneys",
  title: "Chimneys",
  description: "Specialist chimney repairs, structural rebuilding, flaunching renewal, pot fitting, and lead flashing work to eliminate leaks and ensure roofline safety.",
  icon: Flame,
  link: "/ChimneysPage",
  image: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/chimney-job.jpg?v=nfxhvm4bff",
  badge: "Lead & Brick"
}, {
  key: "lead-work",
  title: "Lead Work",
  description: "High-grade sheet lead flashings, chimney soakers, lead aprons, step flashings, valley troughs, and custom lead repairs across Southampton homes.",
  icon: Wrench,
  link: "/LeadWorkPage",
  image: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/chimney-job.jpg?v=nfxhvm4bff",
  badge: "Lead Craft"
}, {
  key: "repointing",
  title: "Repointing",
  description: "Traditional mortar raking and precision repointing for chimney stacks, gable apexes, and parapet brickwork to stop moisture penetration and frost damage.",
  icon: Hammer,
  link: "/RePointingServicesPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1662274/img/modern_brickwork_repointing.png",
  badge: "Masonry Care"
}, {
  key: "roof-cleaning",
  title: "Roof Cleaning",
  description: "Gentle manual scraping, targeted moss removal, biocidal treatments, and protective tile coating to clear gutters and restore your roof tiles safely.",
  icon: Sparkles,
  link: "/RoofCleaningPage",
  image: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/roofing-before-and-after-cleaning.jpg?v=iubj9xvr7t",
  badge: "Moss Removal"
}];
export default function OurSignatureSolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223689" id="expert-solutions" ref={ref} className="bg-background text-foreground py-16 sm:py-24 lg:py-[110px] px-6 lg:px-12">
      <div className={`max-w-[1280px] mx-auto transition-all duration-1000 ease-out motion-reduce:transition-none motion-reduce:transform-none ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-primary mb-3">
            EXPERT SOLUTIONS
          </p>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-secondary-foreground mb-4">
            Roofing Solutions in Southampton
          </h2>
          <p className="font-sans text-base sm:text-lg text-foreground/80 leading-relaxed">
            Delivering structural roofing excellence, weather-resistant flat
            roof systems, and complete roofline maintenance for homeowners
            across Southampton, Hampshire &amp; surrounding areas.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 lg:gap-8">
          {roofingServices.map((service, i) => {
          const Icon = service.icon;
          return <div key={service.key} data-index={i} className="group relative flex flex-col overflow-hidden rounded-[14px] border border-border bg-card shadow-[0_4px_20px_-2px_rgba(21,25,29,0.06)] transition-all duration-300 motion-reduce:transition-none hover:-translate-y-1.5 motion-reduce:hover:translate-y-0 hover:shadow-[0_12px_30px_-4px_rgba(21,25,29,0.12)] hover:border-primary/50">
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-secondary">
                  <img src={service.image} alt={`${service.title} in Southampton by Firststop Roofing Ltd`} className="h-full w-full object-cover transition-transform duration-500 motion-reduce:transition-none group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <span className="absolute bottom-3 left-4 inline-flex items-center rounded-md bg-secondary/95 px-2.5 py-1 text-xs font-semibold text-secondary-foreground shadow-sm">
                    {service.badge}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-6 sm:p-7">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-primary transition-transform duration-300 motion-reduce:transition-none group-hover:scale-105">
                      <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                    </div>
                    <h3 className="font-default text-xl sm:text-2xl font-bold tracking-tight text-secondary-foreground">
                      {service.title}
                    </h3>
                  </div>

                  <p className="font-sans text-foreground/75 text-sm sm:text-base leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <div className="mt-auto pt-2">
                    <Link to={service.link} className="inline-flex min-h-[44px] items-center gap-2 text-sm sm:text-base font-semibold text-primary transition-colors duration-200 hover:text-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md">
                      <span>Explore Service</span>
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 motion-reduce:transition-none group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}